import { createColumns } from './inquiry-table/columns';
import { DataTable } from '@/components/ui/table/data-table';
import { useGetInquiries } from '@/services/inquiry/queries';
import { DataTableSkeleton } from '@/components/ui/table/data-table-skeleton';
import InquiryTableAction from './inquiry-table/inquiry-table-action';
import { Icons } from '@/components/icons';
import { useMemo } from 'react';
import { useInquiryTableFilters } from './inquiry-table/use-inquiry-table-filters';
import type { Inquiry } from '@/services/types';

export default function InquiryTable() {
  const { searchQuery, sort, status, page } = useInquiryTableFilters();

  // Create columns
  const columns = useMemo(() => createColumns(), []);

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    page: page || 1,
    limit: 20,
    ...(searchQuery && { search: searchQuery }),
    ...(sort && { sort: sort as 'asc' | 'desc' }),
    ...(status && { status: status as 'pending' | 'resolved' })
  }), [searchQuery, sort, status, page]);

  const { data, isLoading } = useGetInquiries(filters, {
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  // Memoize data arrays to prevent unnecessary re-renders
  const tableData: Inquiry[] = useMemo(() => data?.data || [], [data?.data]);
  const total = useMemo(() => data?.pagination?.totalDocs ?? 0, [data?.pagination?.totalDocs]);

  if (isLoading) return <DataTableSkeleton />;

  return (
    <div className='h-full space-y-4'>
      <InquiryTableAction />
      <div className='px-4'>
        <DataTable
          columns={columns}
          data={tableData}
          totalItems={total}
          emptyState={
            <div className='flex h-full w-full flex-col items-center justify-center'>
              <Icons.emptyState />
              No inquiries found
            </div>
          }
        />
      </div>
    </div>
  );
}